import React, { useEffect, useState, useCallback, useMemo, memo } from "react";
import { Row, Button } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { FaX } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import instance, { apiEncryptRequest, decryptData } from "../../Api";
import { useAuth } from "../../AuthContext";
import Loader from "../common/Loader";
import AppToast from "../../AppToast";
import toast, { Toaster } from "react-hot-toast";

// ─── Pure helpers ─────────────────────────────────────────────────────────────
const isMobileDevice = () => window.matchMedia("(max-width: 800px)").matches;

// ─── Shared constants ─────────────────────────────────────────────────────────
const BTN_STYLE = {
  width: "7rem", height: "40px", zIndex: 9,
  fontSize: "15px", fontWeight: 700, color: "white",
  backgroundColor: "#e97730", outline: "4px solid #e97730",
  border: "4px solid white", borderRadius: "30px",
};

const TABLE_TOP_IMG    = "/images/admin/08-VM-Billing-List/server-top-img.svg";
const TABLE_MID_IMG    = "/images/admin/08-VM-Billing-List/server-middle-img.svg";
const TABLE_BOTTOM_IMG = "/images/admin/09-Billing-Table/server-bottom-bg.svg";

// ─── Reusable sub-components ─────────────────────────────────────────────────

// Orange action button — replaces 6+ identical copy-pasted buttons
const ActionBtn = memo(({ label, onClick }) => (
  <button
    style={BTN_STYLE}
    onClick={onClick}
    onMouseOver={(e) => { e.currentTarget.style.fontSize = "17px"; }}
    onMouseOut={(e)  => { e.currentTarget.style.fontSize = "15px"; }}
  >
    {label}
  </button>
));

// Table header cell
const THead = memo(({ label }) => (
  <div className="table-head">
    <div className="table-content">{label}</div>
    <img src={TABLE_TOP_IMG} className="bg-image" alt="" />
  </div>
));

// Table data cell
const TData = memo(({ children, emailStyle }) => (
  <div className="table-data">
    <div className="table-content" style={emailStyle}>{children}</div>
    <img src={TABLE_MID_IMG} className="bg-image" alt="" />
  </div>
));

// Table footer cell
const TFoot = memo(() => (
  <div className="table-foot">
    <img src={TABLE_BOTTOM_IMG} className="table-footer-image-size" alt="" />
  </div>
));

// Assign+List button pair — used for both Server and CDN columns
const AssignListPair = memo(({ assignLabel, listLabel, onAssign, onList }) => (
  <div className="row" style={{ marginLeft: "20px" }}>
    <div className="log-in col-md-6" style={{ marginRight: 10, marginLeft: 125 }} onClick={onAssign}>
      <ActionBtn label={assignLabel} onClick={onAssign} />
    </div>
    <div className="log-in col-md-6" style={{ marginTop: -55 }} onClick={onList}>
      <button style={{ ...BTN_STYLE, marginTop: 15 }}
        onMouseOver={(e) => { e.currentTarget.style.fontSize = "17px"; }}
        onMouseOut={(e)  => { e.currentTarget.style.fontSize = "15px"; }}>
        {listLabel}
      </button>
    </div>
  </div>
));

// Eye toggle
const EyeToggle = memo(({ show, onToggle }) =>
  show
    ? <FaEyeSlash onClick={onToggle} style={{ color: "white", width: 20 }} />
    : <FaEye     onClick={onToggle} style={{ color: "white", width: 20 }} />
);

// Create Child User button — same across both mobile and web
const CreateBtn = memo(({ onClick, style }) => (
  <Button style={{ background: "#035189", border: "none", fontSize: 20, padding: "5px 15px", color: "#fff", fontWeight: 600, marginTop: 40, ...style }} onClick={onClick}>
    Create Child User
  </Button>
));

// ─── Single table row — used in both mobile and web ──────────────────────────
const UserRow = memo(({ item, navigate, onDelete }) => (
  <div className="table-row">
    <TData>{item.name}</TData>

    <TData emailStyle={{ textOverflow: "ellipsis", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", maxWidth: 260, lineHeight: "1.2em", wordBreak: "break-all", overflowWrap: "break-word" }}>
      {item.email}
    </TData>

    <TData>{item.role_name || "Child User"}</TData>

    {/* Server */}
    <TData>
      <AssignListPair
        assignLabel="Assign"
        listLabel="VM List"
        onAssign={() => navigate("/vm-assign", { state: { userDetail: item, isServerOrCDN: "SERVER" } })}
        onList={()   => navigate("/assigned-vm",  { state: { userDetail: item } })}
      />
    </TData>

    {/* CDN */}
    <TData>
      <AssignListPair
        assignLabel="Assign"
        listLabel="CDN List"
        onAssign={() => navigate("/vm-assign", { state: { userDetail: item, isServerOrCDN: "CDN" } })}
        onList={()   => navigate("/assigned-cdn", { state: { userDetail: item } })}
      />
    </TData>

    {/* Support */}
    <TData>
      <ActionBtn label="Assign" onClick={() => navigate("/user-create-ticket", { state: { userDetail: item, isSetting: true } })} />
    </TData>

    {/* Delete — only in web view, but harmless to always render */}
    <TData>
      <ActionBtn label="Delete User" onClick={() => onDelete(item)} />
    </TData>
  </div>
));

// ─── Main Component ───────────────────────────────────────────────────────────
const ChildUserPage = ({ ip }) => {
  const { smuser }  = useAuth();
  const navigate    = useNavigate();

  const [isMobile, setIsMobile]     = useState(isMobileDevice);
  const [loading, setLoading]       = useState(false);
  const [usersData, setUsersData]   = useState([]);
  const [signupPopUp, setSignupPopUp] = useState(false);

  const [userName,    setUserName]    = useState(null);
  const [roleName,    setRoleName]    = useState(null);
  const [usrEmail,    setUsrEmail]    = useState(null);
  const [usrPass,     setUsrPass]     = useState(null);
  const [usrConfPass, setUserConfPass] = useState(null);
  const [showPass,    setShowPass]    = useState(false);
  const [showConfPass, setShowConfPass] = useState(false);

  // ── Toast helper ─────────────────────────────────────────────────────────
  const showToast = useCallback((msg) =>
    toast((t) => <AppToast id={t.id} message={msg} isMobile={isMobile} />),
    [isMobile]);

  // ── API ───────────────────────────────────────────────────────────────────
  const getUsers = useCallback(async () => {
    setLoading(true);
    try {
      const enc = await apiEncryptRequest({ user_id: smuser.id });
      const res = await instance.post("/userlist", enc);
      const data = await decryptData(res.data);
      setUsersData(data[0].users);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [smuser?.id]);

  const deleteUser = useCallback(async (item) => {
    setLoading(true);
    try {
      const enc = await apiEncryptRequest({ user_id: item.id });
      await instance.post("/deleteuser", enc);
      await decryptData; // result unused — just refresh
      getUsers();
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [getUsers]);

  const sendInvitationMail = useCallback(async () => {
    // Validation — in order, early return on first failure
    if (!userName)              { showToast("Please enter your name");              return; }
    if (!roleName)              { showToast("Please enter your Role Name");         return; }
    if (!usrEmail)              { showToast("Please enter your Email");             return; }
    if (!usrPass)               { showToast("Please enter Password");               return; }
    if (!usrConfPass)           { showToast("Please enter Confirm Password");       return; }
    if (usrPass !== usrConfPass){ showToast("Password and Confirm Password do not match!"); return; }

    setLoading(true);
    const regPayload = {
      name: userName, role_name: roleName, ip,
      phone: "", email: usrEmail,
      password: usrPass, password_confirmation: usrConfPass,
      login_by: "manual", is_main: "0", parent_id: smuser.id,
    };
    try {
      const enc = await apiEncryptRequest(regPayload);
      const res = await instance.post("/register", enc);
      const data = await decryptData(res.data);
      showToast(data[0].message);
      if (data[0].success) { setSignupPopUp(false); getUsers(); }
    } catch {
      showToast("An error occurred. Please try again.");
    }
    setLoading(false);
  }, [userName, roleName, ip, usrEmail, usrPass, usrConfPass, smuser?.id, getUsers, showToast]);

  // ── Mount: load once + resize listener ───────────────────────────────────
  // Fix: original had [isMobile] as dep — caused getUsers() to re-fire every
  // time the window was resized, making unnecessary API calls on each resize event.
  useEffect(() => {
    getUsers();
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []); // run once only — getUsers is stable via useCallback

  // ── Shared table structure (used in both mobile + web) ────────────────────
  const HEADERS = ["Name", "Email", "Role Type", "Server", "CDN", "Support", "Action"];

  const tableContent = useMemo(() => (
    <div className="billing-list" style={{ height: "50vh" }}>
      {/* Header */}
      <div className="table-row no-hover">
        {HEADERS.map((h) => <THead key={h} label={h} />)}
      </div>

      {/* Rows */}
      {usersData.map((item, idx) => (
        <UserRow key={item.id ?? idx} item={item} navigate={navigate} onDelete={deleteUser} />
      ))}

      {/* Footer */}
      <div className="table-row no-hover">
        {HEADERS.map((h) => <TFoot key={h} />)}
      </div>
    </div>
  ), [usersData, navigate, deleteUser]);

  // ── Signup popup ─────────────────────────────────────────────────────────
  const SignupPopup = useMemo(() => (
    <div style={{ display: "grid", justifyItems: "center" }}>
      <div style={{ backdropFilter: "blur(25px)", borderRadius: 12, border: "2px solid #e97730", top: "15%", position: "absolute", zIndex: 9999999, width: isMobile ? "80%" : "35%", height: "32rem" }}>
        <div style={{ display: "grid", justifyItems: "center" }}>
          <button style={{ zIndex: 9, position: "absolute", backgroundColor: "transparent", border: "none", right: 0 }} onClick={() => setSignupPopUp(false)}>
            <FaX style={{ marginTop: 5, color: "#e97730", fontSize: 19 }} />
          </button>
          <div className="register-main see-full" style={{ marginTop: -1 }}>
            <div className="bg-img"><img src="/images/blue-box-bg.svg" alt="" /></div>
            <form className="see-full">
              <div className="form-top">
                {[
                  { src: "/images/user-white.svg", placeholder: "Name",           setter: setUserName },
                  { src: "/images/user-white.svg", placeholder: "Role Name",      setter: setRoleName },
                  { src: "/images/email.svg",       placeholder: "Email Address", setter: setUsrEmail },
                ].map(({ src, placeholder, setter }) => (
                  <div key={placeholder} className="input-container">
                    <img src={src} alt="" className="imgIcon-signup" />
                    <input type="text" className="input-signup" placeholder={placeholder} onChange={(e) => setter(e.target.value)} />
                  </div>
                ))}

                {/* Password */}
                <div className="input-container" style={{ marginTop: 15 }}>
                  <img src="/images/lock.svg" alt="" className="imgIcon-signup" />
                  <input type={showPass ? "text" : "password"} className="input-signup" placeholder="Password" onChange={(e) => setUsrPass(e.target.value)} />
                  <EyeToggle show={showPass} onToggle={() => setShowPass((p) => !p)} />
                </div>

                {/* Confirm Password */}
                <div className="input-container" style={{ marginTop: 15 }}>
                  <img src="/images/lock.svg" alt="" className="imgIcon-signup" />
                  <input type={showConfPass ? "text" : "password"} className="input-signup" placeholder="Confirm Password" onChange={(e) => setUserConfPass(e.target.value)} />
                  <EyeToggle show={showConfPass} onToggle={() => setShowConfPass((p) => !p)} />
                </div>
              </div>
            </form>

            <div className="log-in" style={{ justifyContent: "center" }}>
              <button style={{ width: "12rem", height: 50, zIndex: 9, position: "relative", marginTop: 35, fontSize: 15, fontWeight: 700, color: "white", backgroundColor: "#e97730", outline: "4px solid #e97730", border: "4px solid white", borderRadius: 30 }}
                onClick={sendInvitationMail}
                onMouseOver={(e) => { e.currentTarget.style.fontSize = "17px"; }}
                onMouseOut={(e)  => { e.currentTarget.style.fontSize = "15px"; }}>
                Send Invitation Mail
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  ), [isMobile, showPass, showConfPass, sendInvitationMail]);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ width: "100%", minHeight: "65rem", position: "relative", backgroundImage: "url(./main-bg.jpg)", backgroundSize: "cover", backgroundRepeat: "round", backgroundBlendMode: "overlay" }}>
      <div className="apptoast-align">
        <Toaster position={isMobile ? "top-center" : "bottom-right"} reverseOrder={false} />
      </div>

      {loading && (
        <div className="loading-overlay" style={{ zIndex: 999999999 }}>
          <Loader isLoading={loading} />
        </div>
      )}

      {signupPopUp && SignupPopup}

      {isMobile ? (
        /* ── MOBILE ──────────────────────────────────────────────────────── */
        <div style={{ height: "50rem" }}>
          <div className="heading-dotted-bill">Users List <span /></div>

          {usersData.length > 0 && (
            <div style={{ position: "relative", marginLeft: 5 }}>
              <CreateBtn onClick={() => setSignupPopUp(true)} />
            </div>
          )}
          {usersData.length === 0 && !signupPopUp && (
            <div style={{ position: "relative", marginTop: "10rem", textAlign: "center" }}>
              <CreateBtn onClick={() => setSignupPopUp(true)} />
            </div>
          )}

          <div className="features-section-solution">
            <Row>
              <div className="col-md-1" />
              <div className="col-md-10" style={{ marginBottom: "5rem" }}>
                {tableContent}
              </div>
              <div className="col-md-1" />
            </Row>
          </div>
        </div>
      ) : (
        /* ── WEB ─────────────────────────────────────────────────────────── */
        <div className="features-page-solution" style={{ height: "50rem", paddingLeft: "15rem", paddingTop: "4rem" }}>
          <div className="heading-dotted-bill" style={{ marginLeft: 10 }}>Users List <span /></div>

          {usersData.length > 0 && (
            <div style={{ position: "relative", marginLeft: 5 }}>
              <CreateBtn onClick={() => setSignupPopUp(true)} />
            </div>
          )}
          {usersData.length === 0 && !signupPopUp && (
            <div style={{ position: "relative", left: "40%", marginTop: "10rem" }}>
              <CreateBtn onClick={() => setSignupPopUp(true)} />
            </div>
          )}

          {usersData.length > 0 && (
            <div className="features-section-solution">
              <Row>
                <div className="col-md-12">
                  <div style={{ display: "flex", position: "relative", marginTop: "1rem", maxWidth: "100%", overflowX: "auto", whiteSpace: "nowrap" }}>
                    {tableContent}
                  </div>
                </div>
              </Row>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChildUserPage;